import { useEffect, type JSX } from "react";
import { useTranslation } from "react-i18next";

interface ArticleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string | null;
  isDark: boolean;
}

export function ArticleDialog({
  isOpen,
  onClose,
  title,
  description,
  isDark,
}: ArticleDialogProps): JSX.Element {
  const { t } = useTranslation();
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return <></>;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 cursor-pointer"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md animate-in fade-in duration-200" />

      {/* Dialog */}
      <div
        className={`relative w-full max-w-lg rounded-2xl border animate-in zoom-in-95 duration-200 ${
          isDark ? "bg-zinc-950 border-zinc-800" : "bg-white border-gray-200"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 pb-0">
          <h2
            className={`text-xl font-semibold pr-4 ${isDark ? "text-white" : "text-gray-900"}`}
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            className={`shrink-0 p-1.5 rounded-lg cursor-pointer transition-colors ${
              isDark
                ? "text-zinc-500 hover:text-white hover:bg-zinc-900"
                : "text-gray-400 hover:text-gray-900 hover:bg-gray-100"
            }`}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 pt-4 max-h-[70vh] overflow-y-auto">
          {description ? (
            <p
              className={`text-sm leading-relaxed ${isDark ? "text-zinc-400" : "text-gray-600"}`}
            >
              {description}
            </p>
          ) : (
            <p
              className={`text-sm italic ${isDark ? "text-zinc-600" : "text-gray-400"}`}
            >
              {t("components.articleDialog.noDescription")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
